import BaseAvatarPlayer from "./common/player/base-player";
import { AvatarType, AvatarConfig } from "./common/player/type";
import { glyxAvatarConfig } from "../config/glyx.config";
import { nalnaniAvatarConfig } from "../config/nalnani.config";
import { kikizAvatarConfig } from "../config/kikiz.config";
// import { golgolzAvatarConfig } from "../config/golgolz.config";
import type ThreeJSApp from "../app";
import EventEmitter from "events";
import { vroidAvatarConfig } from "../config/vroid.config";
import { gangwhaAvatarConfig } from "../config/gangwha.config";
import { bobbiAvatarConfig } from "../config/bobbi.config";
import { taekwondoAvatarConfig } from "../config/taekwondo.config";

export default class AvatarManager extends EventEmitter {
  private root: ThreeJSApp;
  private currentAvatar: BaseAvatarPlayer | null = null;
  private currentAvatarType: AvatarType | null = null;
  private avatarConfigs: Partial<Record<AvatarType, AvatarConfig>>;

  constructor(root: ThreeJSApp) {
    super();
    this.root = root;

    // 아바타 설정 맵 초기화
    // bambam, goesan, custom 등은 동적으로 로드되므로 Partial 사용
    this.avatarConfigs = {
      'glyx': glyxAvatarConfig,
      'nalnani': nalnaniAvatarConfig,
      // 'golgolz': golgolzAvatarConfig,
      'kikiz': kikizAvatarConfig,
      'vroid': vroidAvatarConfig,
      'gangwha': gangwhaAvatarConfig,
      'bobbi': bobbiAvatarConfig,
      'taekwondo': taekwondoAvatarConfig,
    };
  }

  /**
     * 아바타를 선택하고 로드합니다.
     * @param avatarType 선택할 아바타 타입
     * @returns Promise<BaseAvatarPlayer>
     */
  async selectAvatar(avatarType: AvatarType): Promise<BaseAvatarPlayer> {

    // 현재 아바타가 같은 타입이면 그대로 반환
    if (this.currentAvatarType === avatarType && this.currentAvatar) {
      return this.currentAvatar;
    }

    // 기존 아바타 정리
    if (this.currentAvatar) {
      this.currentAvatar.destroy();
      this.currentAvatar = null;
    }

    // 새 아바타 생성
    const config = this.avatarConfigs[avatarType];
    if (!config) {
      throw new Error(`아바타 타입 '${avatarType}'의 설정을 찾을 수 없습니다.`);
    }

    this.currentAvatar = new BaseAvatarPlayer(this.root, config);
    this.currentAvatarType = avatarType;

    // 아바타 이벤트를 매니저로 전달
    this.setupAvatarEvents();

    return this.currentAvatar;
  }

  /**
   * 현재 아바타를 가져옵니다.
   * @returns BaseAvatarPlayer | null
   */
  getCurrentAvatar(): BaseAvatarPlayer | null {
    return this.currentAvatar;
  }

  /**
   * 현재 아바타 타입을 가져옵니다.
   * @returns AvatarType | null
   */
  getCurrentAvatarType(): AvatarType | null {
    return this.currentAvatarType;
  }

  /**
   * 아바타가 준비되었는지 확인합니다.
   * @returns boolean
   */
  isReady(): boolean {
    return this.currentAvatar?.ready || false;
  }

  /**
   * 아바타 설정을 추가하거나 업데이트합니다.
   * @param avatarType 아바타 타입
   * @param config 아바타 설정
   */
  registerAvatarConfig(avatarType: AvatarType, config: AvatarConfig): void {
    this.avatarConfigs[avatarType] = config;
  }

  /**
   * 사용 가능한 아바타 타입 목록을 가져옵니다.
   * @returns AvatarType[]
   */
  getAvailableAvatarTypes(): AvatarType[] {
    return Object.keys(this.avatarConfigs) as AvatarType[];
  }

  /**
   * 애니메이션을 재생합니다.
   * @param name 시나리오 이름
   */
  play(name: string): void {
    if (!this.currentAvatar) {
      console.warn("⚠️ 아바타가 선택되지 않았습니다.");
      return;
    }
    this.currentAvatar.playScenario(name);
  }

  /**
   * 애니메이션을 중지합니다.
   */
  stop(): void {
    if (!this.currentAvatar) {
      console.warn("⚠️ 아바타가 선택되지 않았습니다.");
      return;
    }
    this.currentAvatar.stop();
  }

  /**
   * 립싱크를 재생합니다.
   * @param blendshapeList 블렌드쉐이프 데이터
   * @param duration 지속 시간
   */
  playLipSync(blendshapeList: number[][], duration?: number): void {
    if (!this.currentAvatar) {
      console.warn("⚠️ 아바타가 선택되지 않았습니다.");
      return;
    }
    this.currentAvatar.playLipSync(blendshapeList, duration);
  }

  /**
   * 립싱크를 중지합니다.
   */
  stopLipSync(): void {
    if (!this.currentAvatar) {
      console.warn("⚠️ 아바타가 선택되지 않았습니다.");
      return;
    }
    this.currentAvatar.stopLipSync();
  }

  /**
   * 시나리오 종료 콜백을 설정합니다.
   * @param callback 콜백 함수
   */
  onEndScenario(callback: (...args: unknown[]) => void): AvatarManager {
    if (!this.currentAvatar) {
      console.warn("⚠️ 아바타가 선택되지 않았습니다.");
      return this;
    }
    this.currentAvatar.onEndScenario(callback);
    return this;
  }

  /**
   * 아바타 업데이트 메서드
   * @param delta 델타 시간
   */
  update(delta: number): void {
    if (this.currentAvatar) {
      this.currentAvatar.update(delta);
    }
  }

  /**
   * 아바타 이벤트 설정
   */
  private setupAvatarEvents(): void {
    if (!this.currentAvatar) return;


    // 아바타 준비 완료 이벤트
    this.currentAvatar.on('ready', () => {
      this.emit('ready', this.currentAvatarType);
    });

    // 로딩 진행률 이벤트
    this.currentAvatar.on('loadProgress', (progressData) => {
      this.emit('loadProgress', progressData);
    });
  }

  /**
   * 아바타 매니저 정리
   */
  destroy(): void {
    if (this.currentAvatar) {
      this.currentAvatar.destroy();
      this.currentAvatar = null;
    }
    this.currentAvatarType = null;
    this.removeAllListeners();
  }
} 